import { useEffect, useState, type ReactNode } from 'react';
import { Button, Checkbox, Dropdown, Typography } from 'antd';
import { HolderOutlined, SettingOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** A column the user can show, hide and move: a field definition, or a page's own extra column. */
export interface ColumnOption {
  key: string;
  label: string;
  showInTable?: boolean;
}

/**
 * Visible-column state for a table, persisted per view in localStorage. The list order is the column order.
 * `migrate` reads an older saved list (adding columns that used to be always shown) when this key has none yet.
 */
export function useVisibleColumns(storageKey: string, defs: ColumnOption[] | undefined, migrate?: { from: string; add: string[] }) {
  const [visible, setVisible] = useState<string[] | null>(null);
  useEffect(() => {
    if (!defs) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        setVisible(JSON.parse(saved) as string[]);
        return;
      }
      const old = migrate && localStorage.getItem(migrate.from);
      if (old) {
        setVisible([...(JSON.parse(old) as string[]), ...migrate.add]);
        return;
      }
    } catch {
      /* ignore */
    }
    setVisible(defs.filter((d) => d.showInTable).map((d) => d.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defs, storageKey]);
  const update = (keys: string[]) => {
    setVisible(keys);
    try {
      localStorage.setItem(storageKey, JSON.stringify(keys));
    } catch {
      /* ignore */
    }
  };
  return { visible: visible ?? [], setVisible: update };
}

/** The visible columns, in the user's order. */
export function orderedFields<T extends ColumnOption>(defs: T[], visible: string[]) {
  const byKey = new Map(defs.map((d) => [d.key, d]));
  return visible.map((k) => byKey.get(k)).filter((d): d is T => !!d);
}

const useDragSensors = () => useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

const moveKey = (visible: string[], { active, over }: DragEndEvent) =>
  over && active.id !== over.id ? arrayMove(visible, visible.indexOf(String(active.id)), visible.indexOf(String(over.id))) : null;

/**
 * Wrap a Table to let its field columns be dragged into a new order by their headers.
 * Give those columns `onHeaderCell: () => draggableHeader(key)` and the table `components={sortableHeaderComponents}`.
 */
export function SortableColumns({ visible, onChange, children }: { visible: string[]; onChange: (keys: string[]) => void; children: ReactNode }) {
  const sensors = useDragSensors();
  return (
    <DndContext sensors={sensors} onDragEnd={(e) => { const next = moveKey(visible, e); if (next) onChange(next); }}>
      <SortableContext items={visible} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

export const draggableHeader = (key: string) => ({ 'data-col': key }) as React.HTMLAttributes<HTMLElement>;

type HeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement> & { 'data-col'?: string };

function HeaderCell(props: HeaderCellProps) {
  if (!props['data-col']) return <th {...props} />;
  return <DraggableHeaderCell {...props} />;
}

function DraggableHeaderCell({ 'data-col': col, style, ...props }: HeaderCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col! });
  const { role: _role, ...attrs } = attributes;
  const cellStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform && { ...transform, y: 0 }),
    transition,
    cursor: 'grab',
    ...(isDragging ? { position: 'relative', zIndex: 9, background: '#eef2fe', cursor: 'grabbing' } : {}),
  };
  return <th {...props} ref={setNodeRef} style={cellStyle} {...attrs} {...listeners} />;
}

export const sortableHeaderComponents = { header: { cell: HeaderCell } };

function ChooserRow({ id, label, onHide }: { id: string; label: string; onHide: () => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', transform: CSS.Translate.toString(transform && { ...transform, x: 0 }), transition, background: isDragging ? '#eef2fe' : undefined, position: 'relative', zIndex: isDragging ? 1 : undefined, borderRadius: 4 }}
    >
      <span ref={setActivatorNodeRef} {...attributes} {...listeners} style={{ cursor: 'grab', color: '#8c8c8c', padding: '0 2px' }} aria-label={`Move ${label}`}>
        <HolderOutlined />
      </span>
      <Checkbox checked onChange={onHide}>{label}</Checkbox>
    </div>
  );
}

/** Columns menu: drag the shown columns into order, tick or untick to show or hide them. */
export function ColumnChooser({ defs, visible, onChange }: { defs: ColumnOption[]; visible: string[]; onChange: (keys: string[]) => void }) {
  const sensors = useDragSensors();
  const shown = orderedFields(defs, visible);
  const hidden = defs.filter((d) => !visible.includes(d.key));
  return (
    <Dropdown
      trigger={['click']}
      popupRender={() => (
        <div style={{ background: '#fff', boxShadow: '0 6px 16px rgba(0,0,0,.12)', borderRadius: 8, padding: 12, maxHeight: 420, overflow: 'auto', minWidth: 240 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Shown · drag to reorder</Typography.Text>
          <DndContext sensors={sensors} onDragEnd={(e) => { const next = moveKey(visible, e); if (next) onChange(next); }}>
            <SortableContext items={shown.map((d) => d.key)} strategy={verticalListSortingStrategy}>
              {shown.map((d) => (
                <ChooserRow key={d.key} id={d.key} label={d.label} onHide={() => onChange(visible.filter((k) => k !== d.key))} />
              ))}
            </SortableContext>
          </DndContext>
          {hidden.length > 0 && (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Hidden</Typography.Text>
              {hidden.map((d) => (
                <div key={d.key} style={{ padding: '3px 0 3px 22px' }}>
                  <Checkbox checked={false} onChange={() => onChange([...visible, d.key])}>{d.label}</Checkbox>
                </div>
              ))}
            </>
          )}
          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 10, paddingTop: 8 }}>
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onChange(defs.filter((d) => d.showInTable).map((d) => d.key))}>
              Reset to default columns
            </Button>
          </div>
        </div>
      )}
    >
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Dropdown>
  );
}
